import React from 'react';
import AdvancedSEOHead from "@/components/seo/AdvancedSEOHead";
import { absoluteUrl } from '@/lib/seo';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { CheckCircle, Calendar, TrendingUp, Shield, Target, BarChart3 } from 'lucide-react';

const ServicesBuyboxSprint = () => {
  const seoData = {
    title: "Sprint Buy Box & Catalog Health 15j — Cosmos Wave | Résultats garantis",
    description: "Sprint intensif 15 jours pour optimiser Buy Box et Catalog Health Amazon. Méthode éprouvée, KPI mesurables, résultats garantis. Récupérez vos positions perdues.",
    breadcrumbs: [
      { name: "Accueil", url: "/" },
      { name: "Services", url: "/services" },
      { name: "Sprint Buy Box", url: "/services/buybox-sprint" }
    ],
    serviceSchema: {
      serviceType: "Sprint Buy Box & Catalog Health Amazon",
      areaServed: "France",
      description: "Sprint intensif 15 jours pour restaurer et optimiser le Buy Box Amazon et la santé du catalogue. Méthode data-driven avec résultats mesurables."
    },
    faqSchema: [
      {
        question: "Qu'est-ce que le Sprint Buy Box 15 jours ?",
        answer: "Programme intensif pour restaurer et optimiser votre Buy Box Amazon. Audit complet, corrections techniques, optimisation prix et inventaire, suivi KPI quotidien sur 15 jours."
      },
      {
        question: "Quels résultats puis-je attendre ?",
        answer: "Restauration Buy Box sur 80-95% des ASINs éligibles, amélioration Catalog Health Score, optimisation taux de conversion et visibilité organique Amazon."
      },
      {
        question: "Comment mesurez-vous les résultats ?",
        answer: "KPI quotidiens : % Buy Box obtenu, Catalog Health Score, suppressions corrigées, taux de conversion, CA généré. Dashboard temps réel inclus."
      }
    ]
  };

  return (
    <>
      <AdvancedSEOHead 
        title={seoData.title}
        description={seoData.description}
        breadcrumbs={seoData.breadcrumbs}
        ogImage="/og/buybox-sprint-1200x630.jpg"
        ogType="article"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "Service",
            "name": seoData.serviceSchema.serviceType,
            "description": seoData.serviceSchema.description,
            "areaServed": seoData.serviceSchema.areaServed,
            "provider": {
              "@type": "Organization",
              "name": "Cosmos Wave"
            }
          },
          {
            "@context": "https://schema.org", 
            "@type": "FAQPage",
            "mainEntity": seoData.faqSchema.map(faq => ({
              "@type": "Question",
              "name": faq.question,
              "acceptedAnswer": {
                "@type": "Answer",
                "text": faq.answer
              }
            }))
          }
        ]}
      />
      
      <div className="min-h-screen bg-gradient-to-b from-stone-950 via-stone-900 to-stone-950">
        {/* Hero Section */}
        <section className="relative overflow-hidden pt-24 pb-16">
          <div className="absolute inset-0 bg-gradient-to-r from-violet-600/20 via-orange-500/10 to-violet-600/20"></div>
          <div className="relative mx-auto max-w-6xl px-4">
            <div className="text-center">
              <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
                Sprint Buy Box
                <span className="block text-2xl md:text-4xl text-orange-400 font-normal mt-2">
                  Catalog Health — 15 jours
                </span>
              </h1>
              <p className="text-xl text-stone-300 max-w-3xl mx-auto mb-8">
                Récupérez vos positions Buy Box et optimisez votre Catalog Health en 15 jours. 
                Méthode éprouvée, résultats mesurables, suivi quotidien.
              </p>
              <Button size="lg" className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-3">
                Lancer le sprint
              </Button>
            </div>
          </div>
        </section>

        {/* Méthode Section */}
        <section className="py-16">
          <div className="mx-auto max-w-6xl px-4">
            <h2 className="text-3xl font-bold text-white text-center mb-12">Méthode Sprint 15j</h2>
            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  phase: "Jours 1-3",
                  title: "Audit & Diagnostic",
                  icon: <Target className="h-8 w-8" />,
                  items: [
                    "Analyse SP-API complète",
                    "Mapping suppressions actives",
                    "Audit prix vs concurrence",
                    "Health Score baseline"
                  ]
                },
                {
                  phase: "Jours 4-10",
                  title: "Corrections intensives",
                  icon: <Shield className="h-8 w-8" />,
                  items: [
                    "Corrections techniques urgentes",
                    "Optimisation variations & bundles",
                    "Ajustement stratégie prix",
                    "Monitoring temps réel"
                  ]
                },
                {
                  phase: "Jours 11-15",
                  title: "Optimisation & Suivi",
                  icon: <TrendingUp className="h-8 w-8" />,
                  items: [
                    "Fine-tuning performances",
                    "Optimisation continue",
                    "Rapport final détaillé",
                    "Plan maintien long terme"
                  ]
                }
              ].map((phase, index) => (
                <Card key={index} className="p-6 bg-stone-800/50 border-stone-700">
                  <div className="text-orange-400 mb-4">{phase.icon}</div>
                  <div className="text-sm text-orange-300 font-medium mb-2">{phase.phase}</div>
                  <h3 className="text-xl font-semibold text-white mb-4">{phase.title}</h3>
                  <ul className="space-y-2">
                    {phase.items.map((item, itemIndex) => (
                      <li key={itemIndex} className="flex items-start gap-2 text-stone-300 text-sm">
                        <CheckCircle className="h-4 w-4 text-green-400 mt-0.5 flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Livrables Section */}
        <section className="py-16 bg-stone-900/50">
          <div className="mx-auto max-w-6xl px-4">
            <h2 className="text-3xl font-bold text-white text-center mb-12">Livrables & Résultats</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <Card className="p-6 bg-stone-800/50 border-stone-700">
                <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-3">
                  <BarChart3 className="h-6 w-6 text-orange-400" />
                  Dashboard temps réel
                </h3>
                <ul className="space-y-2">
                  {[
                    "% Buy Box obtenu par ASIN",
                    "Catalog Health Score évolution",
                    "Suppressions actives/corrigées",
                    "Taux de conversion & CA impact"
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-2 text-stone-300">
                      <CheckCircle className="h-4 w-4 text-green-400 mt-1 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </Card>

              <Card className="p-6 bg-stone-800/50 border-stone-700">
                <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-3">
                  <Calendar className="h-6 w-6 text-orange-400" />
                  Suivi quotidien
                </h3>
                <ul className="space-y-2">
                  {[
                    "Points quotidiens 15min",
                    "Rapport hebdomadaire détaillé", 
                    "Ajustements stratégie temps réel",
                    "Support prioritaire WhatsApp"
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-2 text-stone-300">
                      <CheckCircle className="h-4 w-4 text-green-400 mt-1 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </Card>
            </div>
          </div>
        </section>

        {/* KPI Section */}
        <section className="py-16">
          <div className="mx-auto max-w-6xl px-4">
            <h2 className="text-3xl font-bold text-white text-center mb-12">KPI & Objectifs mesurables</h2>
            <div className="grid md:grid-cols-4 gap-6">
              {[
                {
                  metric: "80-95%",
                  label: "Buy Box récupéré",
                  description: "Sur ASINs éligibles"
                },
                {
                  metric: "+25-40%",
                  label: "Health Score",
                  description: "Amélioration moyenne"
                },
                {
                  metric: "48-72h",
                  label: "Corrections majeures",
                  description: "Délai moyen"
                },
                {
                  metric: "15j max",
                  label: "Résultats complets",
                  description: "Garantie délai"
                }
              ].map((kpi, index) => (
                <Card key={index} className="p-6 text-center bg-stone-800/50 border-stone-700">
                  <div className="text-3xl font-bold text-orange-400 mb-2">{kpi.metric}</div>
                  <div className="text-white font-medium mb-1">{kpi.label}</div>
                  <div className="text-sm text-stone-400">{kpi.description}</div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Final */}
        <section className="py-16 bg-gradient-to-r from-orange-600/20 to-violet-600/20">
          <div className="mx-auto max-w-4xl px-4 text-center">
            <h2 className="text-3xl font-bold text-white mb-6">Récupérez vos positions maintenant</h2>
            <p className="text-xl text-stone-300 mb-8">
              Sprint intensif 15 jours avec résultats garantis. Récupérez votre Buy Box et optimisez 
              votre Catalog Health avec notre méthode éprouvée.
            </p>
            <Button size="lg" className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-3">
              Démarrer le Sprint Buy Box
            </Button>
          </div>
        </section>
      </div>
    </>
  );
};

export default ServicesBuyboxSprint;